import { useState, useEffect } from 'preact/hooks';
import { getConfig } from '../config';
import { api } from '../api';
import { t, setLocale } from '../i18n';

/** Double opt-in confirmation page — opened from subscribe confirmation email link. */
export function CampaignSubscribeConfirmPage() {
  const [state, setState] = useState<'loading' | 'success' | 'error'>('loading');
  const [shopName, setShopName] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');

  useEffect(() => {
    const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
    const urlLocale = params.get('locale');
    if (urlLocale && ['en', 'de', 'es', 'fr', 'it', 'pt'].includes(urlLocale.toLowerCase())) {
      setLocale(urlLocale.toLowerCase());
    }
    const email = params.get('email');
    const shop = params.get('shop');
    const token = params.get('token');
    const exp = params.get('exp');

    if (!email || !shop || !token || !exp) {
      setState('error');
      setErrorMsg(t('subscribeConfirm.invalidLink', 'Invalid or expired link.'));
      return;
    }

    const config = getConfig();
    if (!config.apiUrl) {
      setState('error');
      setErrorMsg(t('api.genericError'));
      return;
    }

    const query = `email=${encodeURIComponent(email)}&shop=${encodeURIComponent(shop)}&exp=${encodeURIComponent(exp)}&token=${encodeURIComponent(token)}`;
    api(config, `/aff-api/campaign-subscribe-confirm?${query}`, { skipCache: true })
      .then((res) => {
        if (res.status === 200 && (res.result as { ok?: boolean })?.ok) {
          setShopName((res.result as { shop_name?: string })?.shop_name ?? '');
          setState('success');
        } else {
          setState('error');
          setErrorMsg(res.error ?? t('subscribeConfirm.error', 'An error occurred.'));
        }
      })
      .catch(() => {
        setState('error');
        setErrorMsg(t('api.genericError'));
      });
  }, []);

  const boxStyle = 'max-width:480px;margin:2rem auto;padding:2rem;border-radius:var(--sw-radius,0.375rem);border:1px solid var(--sw-border,#dee2e6);background:var(--sw-bg,#fff);text-align:center';

  if (state === 'loading') {
    return (
      <div class="sw-campaign-subscribe-confirm" style={boxStyle}>
        <p style="color:var(--sw-muted,#6c757d);margin:0">{t('subscribeConfirm.processing', 'Processing…')}</p>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div class="sw-campaign-subscribe-confirm" style={boxStyle}>
        <p style="color:var(--sw-danger,#dc3545);margin:0">{errorMsg}</p>
      </div>
    );
  }

  return (
    <div class="sw-campaign-subscribe-confirm" style={boxStyle}>
      <p style="font-size:1.125rem;margin:0;color:var(--sw-text,#212529)">
        {t('subscribeConfirm.success', { shop_name: shopName || t('unsubscribe.shop', 'Shop') })}
      </p>
    </div>
  );
}
